/**
 * Connection Panel Component
 * Allows users to connect to real status server and generate embed URLs
 */

import { useState, useCallback } from 'react';
import { useStatusStore } from '../../../map-editor-features/status';
import styles from './ConnectionPanel.module.css';
import { log, LogCategory } from '../../../map-editor-shared/lib/logger';
import { environment } from '../../../config/environment';

interface ConnectionPanelProps {
  onClose: () => void;
}

export function ConnectionPanel({ onClose }: ConnectionPanelProps) {
  const { connected, connect, disconnect } = useStatusStore();
  const [serverUrl, setServerUrl] = useState(environment.WEBSOCKET_URL);
  const [urlCopied, setUrlCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  // Always get fresh URL to ensure we have the latest query params
  const getCurrentUrl = useCallback(() => {
    return window.location.href;
  }, []);

  // For display purposes, get URL on each render
  const currentUrl = getCurrentUrl();
  const embedCode = `<iframe src="${currentUrl}" width="100%" height="600" frameborder="0"></iframe>`;

  const handleConnect = () => {
    if (connected) {
      disconnect();
    } else {
      // TODO: Implement real server connection with serverUrl
      connect();
    }
  };

  const handleCopyUrl = async () => {
    try {
      // Get fresh URL at copy time to ensure latest query params
      const freshUrl = window.location.href;
      await navigator.clipboard.writeText(freshUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      alert('URL 복사에 실패했습니다.');
    }
  };

  const handleCopyEmbed = async () => {
    try {
      // Get fresh URL at copy time
      const freshUrl = window.location.href;
      const freshEmbedCode = `<iframe src="${freshUrl}" width="100%" height="600" frameborder="0"></iframe>`;
      await navigator.clipboard.writeText(freshEmbedCode);
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch (err) {
      alert('Embed 코드 복사에 실패했습니다.');
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>서버 연결 & 공유</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.content}>
          {/* Server Connection Section */}
          <section className={styles.section}>
            <h3>실시간 상태 서버 연결</h3>
            <div className={styles.connectionStatus}>
              <span className={`${styles.statusDot} ${connected ? styles.connected : styles.disconnected}`} />
              <span>{connected ? '연결됨' : '연결 안됨'}</span>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="serverUrl">서버 URL</label>
              <input
                id="serverUrl"
                type="text"
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder={environment.WEBSOCKET_URL || 'ws://localhost:7000/status'}
                disabled={connected}
                className={styles.input}
              />
            </div>

            <button
              onClick={handleConnect}
              className={`${styles.button} ${connected ? styles.buttonDanger : styles.buttonPrimary}`}
            >
              {connected ? '연결 해제' : '서버 연결'}
            </button>
          </section>

          {/* Viewer URL Section */}
          <section className={styles.section}>
            <h3>뷰어 공유 URL</h3>
            <p className={styles.description}>
              현재 보고 있는 뷰어 화면을 공유합니다. (선택한 층 포함)
            </p>

            <div className={styles.urlBox}>
              <code className={styles.url}>{currentUrl}</code>
              <button
                onClick={handleCopyUrl}
                className={styles.copyButton}
              >
                {urlCopied ? '✓ 복사됨' : '📋 복사'}
              </button>
            </div>
          </section>

          {/* Embed Code Section */}
          <section className={styles.section}>
            <h3>Embed 코드</h3>
            <p className={styles.description}>
              웹사이트에 뷰어를 임베드하려면 아래 코드를 사용하세요.
            </p>

            <div className={styles.codeBox}>
              <pre className={styles.code}>{embedCode}</pre>
              <button
                onClick={handleCopyEmbed}
                className={styles.copyButton}
              >
                {embedCopied ? '✓ 복사됨' : '📋 복사'}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
